import { getBrandAssetDir } from '../../config/tenant';
import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  Paper,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTenant } from '../../contexts/TenantContext';

const CRIMSON = '#8B0000';

interface SignInProps {
  onForgotPassword?: () => void;
  onSignUp?: () => void;
}

export const SignIn: React.FC<SignInProps> = ({ onForgotPassword, onSignUp }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const { signIn } = useAuth();
  const { config, organizationId } = useTenant();
  const { terminology } = config;
  const brandLogo = `/brand/${getBrandAssetDir(organizationId)}/logo.png`;
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // After login, return to where the user came from (e.g. a /vouch/:id link).
  // Only allow internal paths to avoid open-redirects.
  const returnTo = new URLSearchParams(location.search).get('returnTo');
  const postLoginPath =
    returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/home';

  const validateForm = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    if (!validateForm()) return;

    try {
      setLoading(true);
      await signIn(email, password);
      navigate(postLoginPath);
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.includes('not confirmed') || msg.includes('UserNotConfirmedException')) {
        setErrorMessage('Please verify your email before signing in.');
      } else if (msg.includes('Incorrect') || msg.includes('NotAuthorizedException')) {
        setErrorMessage('Invalid email or password. Please try again.');
      } else if (msg) {
        setErrorMessage(msg);
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    if (onForgotPassword) onForgotPassword();
    else navigate('/forgot-password');
  };

  const handleSignUp = () => {
    if (onSignUp) onSignUp();
    else navigate('/signup');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
      }}
    >
      {/* Hero Section */}
      <Box
        sx={{
          flex: isMobile ? 'none' : 1,
          minHeight: isMobile ? 280 : '100vh',
          position: 'relative',
          backgroundImage: `url(/brand/${getBrandAssetDir(organizationId)}/hero.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          p: 0,
        }}
      >
        {/* Dark overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: isMobile
              ? 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.7) 100%)'
              : 'linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.65) 70%, rgba(0,0,0,0.8) 100%)',
          }}
        />

        {/* Hero content */}
        <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 3, md: 5 }, pb: { xs: 4, md: 6 } }}>
          <Box
            component="img"
            src={brandLogo}
            alt={config.appName}
            sx={{
              height: { xs: 48, md: 64 },
              mb: 3,
              filter: 'brightness(0) invert(1)',
            }}
          />
          <Typography
            variant="h3"
            sx={{
              color: '#FFFFFF',
              fontWeight: 700,
              mb: 1.5,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              lineHeight: 1.2,
            }}
          >
            Connect. Engage. Lead. Together.
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.85)',
              fontWeight: 400,
              maxWidth: 480,
              lineHeight: 1.5,
              fontSize: { xs: '0.95rem', md: '1.15rem' },
            }}
          >
            The modern platform for {terminology.memberPlural.toLowerCase()} — events, messaging, surveys, groups, and more. All in one place.
          </Typography>

          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 3, mt: 4, flexWrap: 'wrap' }}>
              {[
                { num: 'Events', desc: 'Create & sell tickets' },
                { num: 'Groups', desc: 'Organize & chat' },
                { num: 'Community', desc: `Connect with ${terminology.memberPlural.toLowerCase()}` },
              ].map(item => (
                <Box key={item.num} sx={{ textAlign: 'left' }}>
                  <Typography sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1rem' }}>
                    {item.num}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
                    {item.desc}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Sign In Form */}
      <Box
        sx={{
          flex: isMobile ? 1 : '0 0 460px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, md: 5 },
          bgcolor: '#FFFFFF',
        }}
      >
        <Paper
          elevation={0}
          component="section"
          aria-labelledby="signin-title"
          sx={{ width: '100%', maxWidth: 400 }}
        >
          <Box component="header" sx={{ mb: 4 }}>
            {isMobile && (
              <Box
                component="img"
                src={brandLogo}
                alt={config.appName}
                sx={{ height: 40, mb: 2 }}
              />
            )}
            <Typography
              id="signin-title"
              variant="h5"
              component="h1"
              color="text.primary"
              fontWeight={700}
              gutterBottom
            >
              Welcome back
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to your {config.appName} account
            </Typography>
          </Box>

          {errorMessage && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setErrorMessage('')}
              role="alert"
              aria-live="assertive"
            >
              {errorMessage}
            </Alert>
          )}

          <form onSubmit={handleSubmit} aria-label="Sign in form">
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                if (errors.email) setErrors({ ...errors, email: undefined });
              }}
              error={!!errors.email}
              helperText={errors.email}
              margin="normal"
              autoComplete="email"
              autoFocus
              disabled={loading}
              required
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                if (errors.password) setErrors({ ...errors, password: undefined });
              }}
              error={!!errors.password}
              helperText={errors.password}
              margin="normal"
              autoComplete="current-password"
              disabled={loading}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(s => !s)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ textAlign: 'right', mt: 1, mb: 3 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={handleForgotPassword}
                sx={{ cursor: 'pointer' }}
                disabled={loading}
              >
                Forgot Password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mb: 2,
                py: 1.5,
                bgcolor: CRIMSON,
                '&:hover': { bgcolor: '#6B0000' },
                fontWeight: 600,
                fontSize: '1rem',
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} color="inherit" />
                  <span style={{ marginLeft: 8 }}>Signing in...</span>
                </>
              ) : (
                'Sign In'
              )}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Don't have an account?{' '}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleSignUp}
                  sx={{ cursor: 'pointer', fontWeight: 600, color: CRIMSON }}
                  disabled={loading}
                >
                  Sign Up
                </Link>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Box>
    </Box>
  );
};

export default SignIn;
